import React, { useCallback } from 'react'

import { LuCheck, LuChevronDown, LuUsers } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { COLOR_CLASSNAMES } from '@/constants/color'

import {
  ENTITY_PRIORITY_OPTION_COLORS,
  RELATIONSHIP_STATUS_OPTIONS,
  type RelationshipStatus,
  type RelationshipStatusColor,
} from '@/types/library/Entity'

interface RelationshipStatusBadgeProps {
  status: RelationshipStatus | null
  onStatusChange?: (status: RelationshipStatus) => void
  onClick?: () => void
  isLoading?: boolean
  disabled?: boolean
  size?: 'sm' | 'md'
  className?: string
  dropdownContentProps?: React.ComponentProps<typeof DropdownMenuContent>
}

interface PriorityBadgeProps {
  priority: string | null
  onPriorityChange?: (priority: string) => void
  isLoading?: boolean
  disabled?: boolean
  size?: 'sm' | 'md'
  className?: string
  dropdownContentProps?: React.ComponentProps<typeof DropdownMenuContent>
  showLabelSuffix?: boolean
}

interface StatusConfig {
  label: string
  color: RelationshipStatusColor
}

const getStatusConfig = (status: string | null): StatusConfig | null => {
  const option = RELATIONSHIP_STATUS_OPTIONS.find((o) => o.value === status)
  if (option) {
    return {
      label: option.label,
      color: option.color,
    }
  }
  return null
}

// Map color names to dot colors (for dropdown)
const DOT_COLORS: Record<string, string> = {
  'gray-light': 'bg-gray-400',
  'slate-light': 'bg-slate-400',
  'zinc-light': 'bg-zinc-400',
  'blue-light': 'bg-blue-500',
  'cyan-light': 'bg-cyan-500',
  'green-light': 'bg-green-500',
  'amber-light': 'bg-amber-500',
  'yellow-light': 'bg-amber-500',
  'orange-light': 'bg-orange-500',
  'red-light': 'bg-red-500',
  'purple-light': 'bg-purple-500',
}

const EMPTY_PRIORITY_CLASSES =
  'bg-primary-50 border border-primary-200 text-primary-700 dark:bg-primary-700 dark:border-primary-500 dark:text-primary-50'

export const RelationshipStatusBadge: React.FC<
  RelationshipStatusBadgeProps
> = ({
  status,
  onStatusChange,
  onClick,
  isLoading = false,
  disabled = false,
  size = 'sm',
  className,
  dropdownContentProps,
}) => {
  const config = getStatusConfig(status)

  const handleStatusChange = useCallback(
    (value: RelationshipStatus) => () => {
      if (onStatusChange) {
        onStatusChange(value)
      }
    },
    [onStatusChange],
  )

  if (!config) {
    return null
  }

  const colorClasses = COLOR_CLASSNAMES[config.color]

  // Size variants
  // sm: Compact style matching OurCompanyBadge and PendingBadge (for tables)
  // md: Original larger style (for headers/detail pages)
  const sizeClasses =
    size === 'sm' ? 'text-[13px] h-[22px] px-2' : 'text-sm h-7 px-2.5'

  const baseBadgeClasses =
    'inline-flex items-center rounded-md font-medium whitespace-nowrap'

  const {
    className: dropdownContentClassName,
    align = 'start',
    ...restDropdownContentProps
  } = dropdownContentProps ?? {}

  // If no onStatusChange, render as a static badge
  if (!onStatusChange || disabled) {
    const badge = onClick ? (
      <button
        type="button"
        onClick={onClick}
        className={twMerge(
          baseBadgeClasses,
          sizeClasses,
          colorClasses,
          'cursor-pointer hover:opacity-80 transition-opacity',
          className,
        )}
      >
        {config.label}
      </button>
    ) : (
      <span
        className={twMerge(
          baseBadgeClasses,
          sizeClasses,
          colorClasses,
          className,
        )}
      >
        {config.label}
      </span>
    )

    // Wrap with tooltip for small size to explain what the badge represents
    if (size === 'sm') {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent>
            {onClick
              ? 'Relationship Status (click to filter)'
              : 'Relationship Status'}
          </TooltipContent>
        </Tooltip>
      )
    }

    return badge
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isLoading}
          className={twMerge(
            baseBadgeClasses,
            sizeClasses,
            colorClasses,
            'cursor-pointer hover:opacity-80 transition-opacity',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary-500',
            isLoading && 'opacity-50 cursor-wait',
            className,
          )}
        >
          <span>{config.label}</span>
          <LuChevronDown
            size={size === 'sm' ? 12 : 14}
            className="ml-1 opacity-60"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className={twMerge('w-44 p-1', dropdownContentClassName)}
        {...restDropdownContentProps}
      >
        {RELATIONSHIP_STATUS_OPTIONS.map((option) => {
          const isSelected = option.value === status
          const optionDotColor = DOT_COLORS[option.color] ?? 'bg-gray-400'

          return (
            <DropdownMenuItem
              key={option.value}
              onClick={handleStatusChange(option.value)}
              className={twMerge(
                'flex items-center gap-2.5 px-2 py-1.5 cursor-pointer',
                isSelected && 'bg-primary-50 dark:bg-primary-800',
              )}
            >
              <span
                className={twMerge(
                  'w-2 h-2 rounded-full flex-shrink-0',
                  optionDotColor,
                )}
              />
              <span className="flex-1 text-sm">{option.label}</span>
              {isSelected && (
                <LuCheck
                  size={14}
                  className="flex-shrink-0 text-blue-600 dark:text-blue-400"
                />
              )}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export const PriorityBadge: React.FC<PriorityBadgeProps> = ({
  priority,
  onPriorityChange,
  isLoading = false,
  disabled = false,
  size = 'sm',
  className,
  dropdownContentProps,
  showLabelSuffix = false,
}) => {
  const trimmedPriority = priority?.trim()
  const displayPriority = trimmedPriority
    ? `${trimmedPriority}${showLabelSuffix ? ' Priority' : ''}`
    : 'Priority'
  const color = trimmedPriority
    ? (ENTITY_PRIORITY_OPTION_COLORS[trimmedPriority] ?? 'gray-light')
    : 'gray-light'
  const colorClasses = trimmedPriority
    ? COLOR_CLASSNAMES[color]
    : EMPTY_PRIORITY_CLASSES
  const sizeClasses =
    size === 'sm' ? 'text-[13px] h-[22px] px-2' : 'text-sm h-7 px-2.5'
  const baseBadgeClasses =
    'inline-flex items-center rounded-md font-medium whitespace-nowrap'

  const {
    className: dropdownContentClassName,
    align = 'start',
    ...restDropdownContentProps
  } = dropdownContentProps ?? {}
  const handlePriorityChange = useCallback(
    (value: string) => () => {
      if (onPriorityChange) {
        onPriorityChange(value)
      }
    },
    [onPriorityChange],
  )

  const badge =
    disabled || !onPriorityChange ? (
      <span
        className={twMerge(
          baseBadgeClasses,
          sizeClasses,
          colorClasses,
          className,
        )}
      >
        {displayPriority}
      </span>
    ) : (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isLoading}
            className={twMerge(
              baseBadgeClasses,
              sizeClasses,
              colorClasses,
              'cursor-pointer hover:opacity-80 transition-opacity',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary-500',
              isLoading && 'opacity-50 cursor-wait',
              className,
            )}
          >
            <span>{displayPriority}</span>
            <LuChevronDown
              size={size === 'sm' ? 12 : 14}
              className="ml-1 opacity-60"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className={twMerge('w-40 p-1', dropdownContentClassName)}
          {...restDropdownContentProps}
        >
          {Object.entries(ENTITY_PRIORITY_OPTION_COLORS).map(
            ([option, optionColor]) => {
              const isSelected = option === trimmedPriority

              return (
                <DropdownMenuItem
                  key={option}
                  onClick={handlePriorityChange(option)}
                  className={twMerge(
                    'flex items-center gap-2.5 px-2 py-1.5 cursor-pointer',
                    isSelected && 'bg-primary-50 dark:bg-primary-800',
                  )}
                >
                  <span
                    className={twMerge(
                      'w-2 h-2 rounded-full flex-shrink-0',
                      DOT_COLORS[optionColor] ?? 'bg-gray-400',
                    )}
                  />
                  <span className="flex-1 text-sm">{option}</span>
                  {isSelected && (
                    <LuCheck
                      size={14}
                      className="flex-shrink-0 text-blue-600 dark:text-blue-400"
                    />
                  )}
                </DropdownMenuItem>
              )
            },
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )

  if (size === 'sm') {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>Priority</TooltipContent>
      </Tooltip>
    )
  }

  return badge
}

// Team Member badge for internal people
export const TeamMemberBadge: React.FC<{
  className?: string
  size?: 'sm' | 'md'
}> = ({ className, size = 'sm' }) => {
  const sizeClasses =
    size === 'sm' ? 'text-[13px] h-[22px] w-[22px]' : 'text-sm h-7 w-7'

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <span
          className={twMerge(
            'inline-flex items-center justify-center rounded-md font-medium',
            sizeClasses,
            COLOR_CLASSNAMES['green-light'],
            className,
          )}
        >
          <LuUsers
            className={twMerge(
              'flex-shrink-0',
              size === 'sm' ? '!w-[13px] !h-[13px]' : '!w-4 !h-4',
            )}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent>Team Member</TooltipContent>
    </Tooltip>
  )
}
